export const RECIPE_ANALYTICS_EVENTS=Object.freeze([
  'recipe_list_opened',
  'recipe_viewed',
  'recipe_saved',
  'recipe_unsaved',
  'recipe_use_started',
  'recipe_draft_created',
  'recipe_session_started',
  'recipe_session_completed',
  'quantity_adaptation_used',
  'compatibility_warning_shown'
]);

const ALLOWED=new Set(RECIPE_ANALYTICS_EVENTS);
const TYPES=new Set(['official','community','private']);
const SCOPES=new Set(['public','saved','mine']);
const COMPATIBILITY=new Set(['compatible','incompatible','review','unknown']);

function choice(value,allowed,fallback='unknown'){
  const key=String(value??'').trim().toLowerCase();
  return allowed.has(key)?key:fallback;
}

function version(value){
  const number=Number(value);
  return Number.isInteger(number)&&number>0?Math.min(number,1000000):0;
}

function count(value,max=1000){
  const number=Number(value);
  if(!Number.isFinite(number))return 0;
  return Math.max(0,Math.min(max,Math.floor(number)));
}

function sourceClass(type){
  switch(choice(type,TYPES)){
    case 'official': return 'official_catalogue';
    case 'community': return 'community_user';
    case 'private': return 'private_personal';
    default: return 'unknown';
  }
}

function recipeDimensions(recipe){
  const type=choice(recipe?.type,TYPES);
  return Object.freeze({
    recipe_type:type,
    recipe_source:sourceClass(type),
    recipe_version:version(recipe?.version)
  });
}

function sessionRecipeDimensions(session){
  const snapshot=session?.recipeSourceSnapshot||{};
  const type=choice(snapshot.type,TYPES);
  return Object.freeze({
    recipe_type:type,
    recipe_source:sourceClass(type),
    recipe_version:version(session?.recipeVersion),
    recipe_attributed:Boolean(session?.recipeId&&session?.recipeVersion&&snapshot.type)
  });
}

function adaptationProperties(review={}){
  const value=review||{};
  const items=Array.isArray(value.items)?value.items:[];
  const amounts=Array.isArray(value.amounts)?value.amounts:[];
  const processTimes=Array.isArray(value.processTimes)?value.processTimes:[];
  const warnings=Array.isArray(value.warnings)?value.warnings:[];
  return Object.freeze({
    changed_item_count:count(items.filter(item=>item?.changed).length,100),
    scaled_amount_count:count(amounts.filter(item=>item?.automaticallyScaled).length,100),
    manual_override_count:count(
      amounts.filter(item=>item?.manualOverride).length
      +processTimes.filter(item=>item?.manualOverride).length,
      100
    ),
    warning_count:count(warnings.length,100),
    manual_review_required:Boolean(value.requiresManualReview)
  });
}

export function createRecipeAnalytics({analytics=null}={}){
  function track(name,properties={}){
    if(!ALLOWED.has(name))throw new TypeError('Unsupported Recipe analytics event');
    try{
      const result=analytics?.track?.(name,properties);
      return result??Object.freeze({accepted:false,reason:'ANALYTICS_UNAVAILABLE'});
    }catch{
      return Object.freeze({accepted:false,reason:'ANALYTICS_FAILED'});
    }
  }

  function withRecipe(recipe,extra={}){
    return Object.freeze({...recipeDimensions(recipe),...extra});
  }

  return Object.freeze({
    events:RECIPE_ANALYTICS_EVENTS,
    track,
    listOpened({scope='public',offline=false}={}){
      return track('recipe_list_opened',{
        scope:choice(scope,SCOPES,'public'),
        offline:Boolean(offline)
      });
    },
    viewed({recipe,compatibility='unknown',offline=false}={}){
      return track('recipe_viewed',withRecipe(recipe,{
        compatibility:choice(compatibility,COMPATIBILITY),
        offline:Boolean(offline)
      }));
    },
    saved({recipe,offline=false}={}){
      return track('recipe_saved',withRecipe(recipe,{offline:Boolean(offline)}));
    },
    unsaved({recipe,offline=false}={}){
      return track('recipe_unsaved',withRecipe(recipe,{offline:Boolean(offline)}));
    },
    useStarted({recipe,compatibility='unknown',offline=false}={}){
      return track('recipe_use_started',withRecipe(recipe,{
        compatibility:choice(compatibility,COMPATIBILITY),
        offline:Boolean(offline)
      }));
    },
    draftCreated({recipe,offline=false}={}){
      return track('recipe_draft_created',withRecipe(recipe,{offline:Boolean(offline)}));
    },
    sessionStarted({session,offline=false}={}){
      return track('recipe_session_started',{
        ...sessionRecipeDimensions(session),
        offline:Boolean(offline)
      });
    },
    sessionCompleted({session,offline=false}={}){
      return track('recipe_session_completed',{
        ...sessionRecipeDimensions(session),
        offline:Boolean(offline)
      });
    },
    quantityAdaptationUsed({recipe,review,offline=false}={}){
      return track('quantity_adaptation_used',withRecipe(recipe,{
        ...adaptationProperties(review),
        offline:Boolean(offline)
      }));
    },
    compatibilityWarningShown({recipe,compatibility='unknown',offline=false}={}){
      const value=choice(compatibility,COMPATIBILITY);
      if(!['incompatible','review'].includes(value)){
        return Object.freeze({accepted:false,reason:'NO_WARNING'});
      }
      return track('compatibility_warning_shown',withRecipe(recipe,{
        compatibility:value,
        offline:Boolean(offline)
      }));
    }
  });
}
